'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { contactSchema, type ContactFormData } from '@/lib/validations';
import { useSiteData } from '@/lib/dataCache';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

// Fallbacks kept in sync with lib/seo FALLBACK_CONFIG; only used if the
// admin hasn't populated settings or the API fails.
const DEFAULTS = {
  email: 'hello@nasifahammedniloy.com',
  phone: '+880 1947527775',
  address: 'Dhaka, Bangladesh',
  availabilityStatus: 'Currently available for freelance projects',
};

type PublicSettings = Partial<Record<string, string>>;

type AboutSnippet = {
  email: string | null;
  phone: string | null;
  location: string | null;
  availability: string | null;
} | null;

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pull live contact data from the admin-editable sources. Both hooks return
  // their default ({} / null) instantly if the cache is cold, so this never
  // blocks the initial render.
  const settings = useSiteData<PublicSettings>(
    'settings.public',
    '/api/settings/public',
    {}
  );
  const about = useSiteData<AboutSnippet>('about', '/api/about', null);

  const email = settings.email || about?.email || DEFAULTS.email;
  const phone = settings.phone || about?.phone || DEFAULTS.phone;
  const location = settings.address || about?.location || DEFAULTS.address;
  const availabilityText =
    settings.availabilityStatus || about?.availability || DEFAULTS.availabilityStatus;

  const phoneHref = `tel:${phone.replace(/[^\d+]/g, '')}`;
  const mapHref = `https://maps.google.com/?q=${encodeURIComponent(location)}`;

  const INFO_CARDS = [
    { icon: Mail, label: 'Email', value: email, href: `mailto:${email}`, color: '#6C63FF' },
    { icon: Phone, label: 'Phone', value: phone, href: phoneHref, color: '#FF6584' },
    { icon: MapPin, label: 'Location', value: location, href: mapHref, color: '#22C55E' },
  ];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error('Failed to send message');

      toast.success('Message sent! I will get back to you soon.');
      reset();
    } catch {
      toast.error('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-blob contact-blob-1" />
      <div className="contact-blob contact-blob-2" />

      <div className="section-container">
        <motion.div
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <span className="section-label">Get In Touch</span>
          <h2 className="section-title">Let&apos;s Work Together</h2>
          <p className="section-subtitle">
            Have a project in mind or just want to say hi? Drop me a message — I&apos;ll reply within 24 hours.
          </p>
        </motion.div>

        <div className="contact-grid">
          {/* Form */}
          <motion.div
            className="contact-form-wrapper"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form className="contact-form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="contact-form-row">
                <div className="contact-field">
                  <label className="contact-label" htmlFor="name">Name</label>
                  <input
                    id="name"
                    type="text"
                    className={`contact-input ${errors.name ? 'contact-input-error' : ''}`}
                    placeholder="Your full name"
                    {...register('name')}
                  />
                  {errors.name && <span className="contact-error">{errors.name.message}</span>}
                </div>

                <div className="contact-field">
                  <label className="contact-label" htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    className={`contact-input ${errors.email ? 'contact-input-error' : ''}`}
                    placeholder="you@example.com"
                    {...register('email')}
                  />
                  {errors.email && <span className="contact-error">{errors.email.message}</span>}
                </div>
              </div>

              <div className="contact-field">
                <label className="contact-label" htmlFor="subject">Subject</label>
                <input
                  id="subject"
                  type="text"
                  className={`contact-input ${errors.subject ? 'contact-input-error' : ''}`}
                  placeholder="What's this about?"
                  {...register('subject')}
                />
                {errors.subject && <span className="contact-error">{errors.subject.message}</span>}
              </div>

              <div className="contact-field">
                <label className="contact-label" htmlFor="message">Message</label>
                <textarea
                  id="message"
                  rows={6}
                  className={`contact-textarea ${errors.message ? 'contact-input-error' : ''}`}
                  placeholder="Tell me about your project..."
                  {...register('message')}
                />
                {errors.message && <span className="contact-error">{errors.message.message}</span>}
              </div>

              <button
                type="submit"
                className="contact-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="contact-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Info */}
          <motion.div
            className="contact-info"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h3 className="contact-info-title">Contact Info</h3>
            <p className="contact-info-subtitle">
              Prefer a direct line? Reach out through any of these channels.
            </p>

            <div className="contact-info-list">
              {INFO_CARDS.map((card, i) => {
                const Icon = card.icon;
                return (
                  <motion.a
                    key={card.label}
                    href={card.href}
                    className="contact-info-card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                    target={card.icon === MapPin ? '_blank' : undefined}
                    rel={card.icon === MapPin ? 'noopener noreferrer' : undefined}
                  >
                    <div
                      className="contact-info-icon"
                      style={{
                        background: `${card.color}15`,
                        color: card.color,
                      }}
                    >
                      <Icon size={20} />
                    </div>
                    <div className="contact-info-text">
                      <div className="contact-info-label">{card.label}</div>
                      <div className="contact-info-value">{card.value}</div>
                    </div>
                  </motion.a>
                );
              })}
            </div>

            <div className="contact-availability">
              <span className="contact-availability-dot" />
              <span className="contact-availability-text">{availabilityText}</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
